import { getAirportList } from '@/api';
import ICON_GUIDE from '@/assets/airport/icon_guide.png';
import ICON_KIOSK from '@/assets/airport/icon_kiosk.png';
import ICON_RECEIPT from '@/assets/airport/icon_receipt.png';
import ICON_REMITTANCE from '@/assets/airport/icon_remittance.png';
import DetailBillModal from '@/components/MedicalAirport/DetailBillModal';
import ReceiptLayout from '@/components/MedicalReceipt/ReceiptLayout';
import { routes } from '@/constant/routes.const';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState, initialGlobalState } from '@/store';
import { RefundAmount, RefundBillList, TextButton } from '@repo/components';
import { AIRPORT_LIST, SELECT_LIST } from '@repo/constants';
import { Button, Icon } from '@successmode-ktp/kui';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

const MedicalAirport = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { passportNumber, nationality, departureDate } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const selectedAirport =
    passportNumber && localStorage.getItem(passportNumber);

  const [openedBillIndex, setOpenedBillIndex] = useState(-1);

  const { data } = useQuery(
    ['airportList'],
    () => (!!passportNumber ? getAirportList({ passportNumber }) : null),
    { enabled: !!passportNumber },
  );

  const onOpenLogoutModal = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        isActive: true,
        type: 'CUSTOM',
        data: {
          title: nationality === 'KOR' ? '로그아웃 할까요?' : 'Sign Out',
          content:
            nationality === 'KOR'
              ? '다른 여권번호로 조회하려면 로그아웃 하세요.'
              : `Sign out to look up\nanother passport number.`,
          btnText: nationality === 'KOR' ? '로그아웃' : 'Sign Out',
          subBtnText: nationality === 'KOR' ? '취소' : 'Cancel',
          btnCallback: logout,
        },
      },
    });
  };

  const logout = () => {
    dispatch({
      type: 'UPDATE_USER_INFO',
      payload: initialGlobalState,
    });
    localStorage.clear();
    history.replace(routes.medical);
  };

  const nationIndex = nationality === 'KOR' ? 1 : 0;
  const text = {
    contact: ['Contact', '문의하기'],
    myRefund: ['My Refund', '내 환급금'],
    refundGuide: ['How to get a refund?', '환급절차 안내'],
    myReceipts: ['My Receipts', '나의 택스리펀 영수증'],
    kioskLocation: ['Customs Location', '세관 위치'],
    getByRemittance: ['Get refund by Remittance', '송금으로 환급받기'],
    logOut: ['Log out', '로그아웃'],
  };

  return (
    <>
      <ReceiptLayout>
        <ButtonWrapper>
          <Button
            onClick={() => history.push(routes.medicalSelectAirport)}
            size='sm'
            variant='line'
            style={{
              boxShadow: '0px 0px 4px 0px #6A9DFF',
              gap: '6px',
              padding: '0 12px',
              width: 'auto',
            }}
          >
            <Icon.Departure size={20} fillColor='#246CF6' />
            {selectedAirport
              ? SELECT_LIST.find(
                  (v) => v.value === decodeURIComponent(selectedAirport),
                )?.[nationality === 'KOR' ? 'korLabel' : 'label']
              : 'Airport'}
          </Button>
        </ButtonWrapper>
        <RefundAmount
          refundAmount={data?.totalRefundAmount || 0}
          nation={nationality}
          onClick={() => history.push(routes.medicalReceipt)}
        />
        <RefundSection>
          {data?.items && (
            <RefundBillList
              type='medical'
              billList={data.items}
              onClick={setOpenedBillIndex}
              nation={nationality}
            />
          )}
        </RefundSection>
        <DescriptionWrapper>
          <DescriptionButton
            onClick={() => history.push(routes.medicalAirportGuide)}
          >
            <Description>
              <img src={ICON_GUIDE} alt='' width={24} />
              {text.refundGuide[nationIndex]}
            </Description>
            <Icon.ChevronRight size={24} fillColor='#111111' />
          </DescriptionButton>
          <DescriptionButton
            onClick={() => history.push(routes.medicalReceipt)}
          >
            <Description>
              <img src={ICON_RECEIPT} alt='' width={24} />
              {text.myReceipts[nationIndex]}
            </Description>
            <Icon.ChevronRight size={24} fillColor='#111111' />
          </DescriptionButton>
          <DescriptionButton
            onClick={() => history.push(routes.medicalAirportLocation)}
          >
            <Description>
              <img src={ICON_KIOSK} alt='' width={24} />
              {text.kioskLocation[nationIndex]}
            </Description>
            <Icon.ChevronRight size={24} fillColor='#111111' />
          </DescriptionButton>
          <DescriptionButton
            onClick={() => {
              localStorage.setItem(`${passportNumber}-transfer`, 'true');
              if (departureDate) {
                history.replace({
                  pathname: routes.medicalHome,
                });
              } else {
                history.replace({
                  pathname: routes.medicalDeparture,
                });
              }
            }}
          >
            <Description>
              <img src={ICON_REMITTANCE} alt='' width={24} />
              {text.getByRemittance[nationIndex]}
            </Description>
            <Icon.ChevronRight size={24} fillColor='#111111' />
          </DescriptionButton>
        </DescriptionWrapper>
        <BottomSection>
          <TextButton
            title={text.logOut[nationIndex]}
            onClick={onOpenLogoutModal}
            color='#1856CD'
            bgColor='#fff'
            outline
          />
        </BottomSection>
      </ReceiptLayout>
      {openedBillIndex > -1 && data?.items && (
        <DetailBillModal
          billList={data.items}
          openedId={openedBillIndex}
          onClose={() => setOpenedBillIndex(-1)}
        />
      )}
    </>
  );
};

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  margin-bottom: 16px;
`;

const RefundSection = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const DescriptionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 28px;
`;

const DescriptionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0px 0px 20px 0px rgba(174, 191, 204, 0.5);
`;

const Description = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  color: #111;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
`;

const BottomSection = styled.div`
  position: relative;
  width: 100%;
  background-color: #ffffff;
  padding: 28px 0 36px 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% + 40px);
    height: 8px;
    background-color: #f5f6f7;
  }
`;

export default MedicalAirport;
